import { useEffect, useRef } from "react";

const AXIS_KEYWORDS = {
  top: "0%",
  center: "50%",
  bottom: "100%",
  left: "0%",
  right: "100%",
};

function resolveAxis(value, fallback) {
  if (!value) return fallback;
  return AXIS_KEYWORDS[value] || value;
}

function parseBackgroundPosition(position = "center") {
  const parts = String(position).trim().split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    const axis = resolveAxis(parts[0], "50%");
    // Single keyword like "top"/"bottom" applies to the Y axis.
    if (parts[0] === "top" || parts[0] === "bottom") {
      return { x: "50%", y: axis };
    }
    return { x: axis, y: "50%" };
  }

  return {
    x: resolveAxis(parts[0], "50%"),
    y: resolveAxis(parts[1], "50%"),
  };
}

/**
 * Subtle scroll-linked parallax via background-position (not transform).
 * Throttled with requestAnimationFrame to avoid scroll jank.
 */
export function useParallax(speed = 0.12, enabled = true, basePosition = "center") {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const { x, y } = parseBackgroundPosition(basePosition);

    if (reducedMotion || isMobile) {
      element.style.backgroundPosition = `${x} ${y}`;
      return;
    }

    const section = element.parentElement?.parentElement ?? element.parentElement;
    if (!section) return;

    let ticking = false;

    const updatePosition = () => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        const yShift = rect.top * speed;
        element.style.backgroundPosition = `${x} calc(${y} + ${yShift}px)`;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updatePosition);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    updatePosition();

    return () => window.removeEventListener("scroll", onScroll);
  }, [speed, enabled, basePosition]);

  return ref;
}
